import {HashUtils} from "../../HashUtils";

import {IElevenLabsTextToSpeechRequestData} from "./types/IElevenLabsTextToSpeechRequestData";

export interface IElevenLabsSpeechDataCacheStorage {
    add(speechRequestData: IElevenLabsTextToSpeechRequestData, speechData: ArrayBuffer): Promise<void>;

    fetch(speechRequestData: IElevenLabsTextToSpeechRequestData): Promise<ArrayBuffer | undefined>;

    delete(speechRequestData: IElevenLabsTextToSpeechRequestData): Promise<boolean>;

    clearAll(): Promise<number>;
}

/**
 *
 * ElevenLabsSpeechDataCacheStorage
 *
 * In order to not make redundant fetch requests for speech data to the API backend service,
 * Responses are cached in a storage that implements `IElevenLabsSpeechDataCacheStorage`.
 *
 * `ElevenLabsSpeechDataCacheStorage` is the default implementation of this interface uses the Browsers built in
 * Cache API to store the ArrayBuffer that corresponds to a specific `IElevenLabsTextToSpeechRequestData`.
 * Using the request data object as the basis for the storage key we guarantee that we will have
 * unique cache entries for configuration changes such as different voiceIds for the same text.
 *
 * Your backend service should also have a caching step where audio data is stored, ideally using
 * the same strategy for key generation as the client side caching deploys.
 *
 * */
export class ElevenLabsSpeechDataCacheStorage implements IElevenLabsSpeechDataCacheStorage {

    private constructor(private _storageClient: Cache) {
    }

    static async open(cacheName: string = 'vr_11labs_cache'): Promise<IElevenLabsSpeechDataCacheStorage> {

        let storageClient = await window.caches?.open(cacheName);

        if(!storageClient) {
            throw Error('ElevenLabsSpeechDataCacheStorage.open(): window.caches is not supported in this browser.');
        }

        return new ElevenLabsSpeechDataCacheStorage(storageClient);
    }

    async clearAll(): Promise<number> {
        let numberOfDeletions: number = 0;

        const requests = await this._storageClient.keys();
        for (let request of requests) {
            numberOfDeletions++;
            await this._storageClient.delete(request);
        }

        return Promise.resolve(numberOfDeletions);
    }


    async delete(speechRequestData: IElevenLabsTextToSpeechRequestData): Promise<boolean> {
        return this._storageClient.delete(new Request(await this.storageKey(speechRequestData)));
    }

    async fetch(speechRequestData: IElevenLabsTextToSpeechRequestData): Promise<ArrayBuffer | undefined> {
        const response = await this._storageClient.match(new Request(await this.storageKey(speechRequestData)));
        return response?.arrayBuffer();
    }

    async add(speechRequestData: IElevenLabsTextToSpeechRequestData, speechData: ArrayBuffer): Promise<void> {
        return this._storageClient.put(new Request(await this.storageKey(speechRequestData)), new Response(speechData));
    }

    private async storageKey(speechRequestData: IElevenLabsTextToSpeechRequestData): Promise<string> {
        const hashString = await HashUtils.generateSHA256Hash(JSON.stringify(speechRequestData));
        return `/${hashString}`;
    }
}
